import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { ChatModule } from '@nimbus-global/features/chat/chat.module';
import { ChatFormModule } from '@nimbus-global/features/chat-form/chat-form.module';
import { ChatChartModule } from '@nimbus-global/features/chat-chart/chat-chart.module';
import { ChatChartType } from '@nimbus-global/features/chat-chart/models/chat-chart.model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IChatMessage } from '@nimbus-global/features/chat/models/chat.model';
import { ChatFormFieldType, ChatFormOperation, IChatForm, IChatFormField } from '@nimbus-global/features/chat-form/models/chat-form.model';
import { IChatFormFieldAction } from '@nimbus-global/features/chat-form/models/chat-form-resolver.model';
import { ChatFormTenantMode } from '@nimbus-global/features/chat-form/models/chat-form-tenant-resolver';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { CompanionService } from '@nimbus-library/features/companion-chat/services/companion.service';
import {
  CompanionConversationModel,
  CompanionDocumentSourceModel,
  CompanionReferenceType,
} from '@nimbus-library/features/companion-chat/models/companion-conversation.model';
import {
  CompanionChatMessage,
  ICompanionChatMessageFormResolvedEvent,
} from '@nimbus-library/features/companion-chat/models/companion-chat-message.model';
import { ChatMessageFormResolverProxy } from '@nimbus-library/features/companion-chat/services/chat-message-form-resolver-proxy';
import { CompanionChatService } from '@nimbus-library/features/companion-chat/services/companion-chat.service';
import { CompanionSplitScreenService } from '@nimbus-library/features/companion-chat/services/companion-split-screen.service';
import { NEW_CONVERSATION_ID } from '@nimbus-library/features/companion-chat/companion-chat.routes';
import { AppRoutesShared } from '@nimbus-global/features/shared/services/routing/app-routes-shared.const';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { CompanionChatResolver } from '@nimbus-library/features/companion-chat/services/companion-chat-resolver';
import { CompanionChatFormService } from '@nimbus-library/features/companion-chat/services/companion-chat-form.service';

class CompanionChatMessagesState {
  public constructor(
    public pageSize: number = 20,
    public skip: string | null = null,
  ) {}
}

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-companion-chat-panel',
  templateUrl: './companion-chat-panel.component.html',
  styleUrls: ['./companion-chat-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, ChatModule, ChatFormModule, ChatChartModule, SharedUIModule],
})
export class CompanionChatPanelComponent implements OnInit {
  @Input() public splitScreen = false;
  @HostBinding('class.collapsed') public isChatCollapsed = false;

  private readonly newMessageIndex = 0;
  private conversationId: string = null;
  private readonly isNewConversationSubject = new BehaviorSubject<boolean>(true);
  // Flag to skip the next paramMap-triggered load after navigating to a newly created conversation.
  // When the first message is sent, the response returns a new conversationId and we navigate to it.
  // This navigation causes paramMap to emit, which would normally reload messages — but they are
  // already displayed, so we skip that redundant load and just reset the flag.
  private skipNextLoad = false;
  private messagesState = new CompanionChatMessagesState();
  private readonly conversationChange$ = new Subject<void>();
  private readonly loadTrigger$ = new Subject<{ reset: boolean }>();
  private activeAgentMessage?: { subscription: Subscription; rollback: () => void };
  private readonly formResolver: CompanionChatFormService;

  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;
  public readonly ChatChartType = ChatChartType;

  public userName = '';
  public messages$ = new BehaviorSubject<CompanionChatMessage[]>([]);
  public hasPendingAction$: Observable<boolean> = this.messages$.pipe(
    map((messages) => messages.some((m) => m.form && m.form.values === undefined)),
  );
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public isInputDisabled$ = new BehaviorSubject<boolean>(false);
  public isAwaitingAgentMessage$ = new BehaviorSubject<boolean>(false);
  public isSplitScreenFeatureEnabled$: Observable<boolean>;
  public readonly isNewConversation$ = this.isNewConversationSubject.asObservable();

  public readonly suggestions = [
    { category: TranslationKeys.CompanionChatSuggestion1Category, text: TranslationKeys.CompanionChatSuggestion1 },
    { category: TranslationKeys.CompanionChatSuggestion2Category, text: TranslationKeys.CompanionChatSuggestion2 },
    { category: TranslationKeys.CompanionChatSuggestion3Category, text: TranslationKeys.CompanionChatSuggestion3 },
  ];

  private get isNewConversation(): boolean {
    return this.isNewConversationSubject.value;
  }

  private set isNewConversation(value: boolean) {
    this.isNewConversationSubject.next(value);
  }

  public get hasMoreMessages(): boolean {
    return !!this.messagesState.skip;
  }

  public get isSplitScreenDisabled$(): Observable<boolean> {
    return this.companionSplitScreenService.isSplitScreenDisabled$;
  }

  public get toggleSplitScreenAriaLabel(): TranslationKeys {
    return this.splitScreen ? TranslationKeys.CompanionChatExpandScreen : TranslationKeys.CompanionChatSplitScreen;
  }

  public get toggleCollapseAriaLabel(): TranslationKeys {
    return this.isChatCollapsed ? TranslationKeys.CompanionChatExpand : TranslationKeys.CompanionChatCollapse;
  }

  public constructor(
    private readonly authenticationService: IAuthenticationService,
    private readonly companionService: CompanionService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly companionChatService: CompanionChatService,
    private readonly companionSplitScreenService: CompanionSplitScreenService,
    private readonly featureFlippingService: FeatureFlippingService,
    private readonly toastrService: ToastrService,
    private readonly translateService: TranslateService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly companionChatResolver: CompanionChatResolver,
  ) {
    this.formResolver = new CompanionChatFormService(this.companionChatResolver.formResolvers);
  }

  public ngOnInit(): void {
    this.userName = this.authenticationService.getUserInfo().name;
    this.isSplitScreenFeatureEnabled$ = this.featureFlippingService.isEnabledInFeatureManagement(Feature.CompanionConversationSplitScreen);

    this.initialize();
  }

  public onMessageSent(message: string): void {
    if (this.activeAgentMessage) {
      return;
    }
    const messagesCurrent = this.messages$.value;
    const messageUser = this.companionChatService.createUserMessage(messagesCurrent.length, message);
    const messageTyping = this.companionChatService.createAgentTypingMessage(messagesCurrent.length + 1);

    this.pushMessages([...messagesCurrent, messageUser, messageTyping]);

    if (this.isNewConversation) {
      this.isInputDisabled$.next(true);
    }

    const clientHints = this.companionChatService.buildClientHints(this.route);

    this.companionSplitScreenService.isSplitScreenDisabled$.next(true);
    const wasNewConversation = this.isNewConversation;
    const rollback = (): void => {
      if (wasNewConversation) {
        this.pushMessages([]);
        return;
      }
      this.pushMessages(this.messages$.value.filter((m) => m.id !== messageUser.id && m.id !== messageTyping.id));
    };
    this.isAwaitingAgentMessage$.next(true);
    const subscription = this.companionService
      .postConversation({ query: message, clientHints }, this.conversationId)
      .pipe(
        takeUntil(this.conversationChange$),
        finalize(() => {
          this.companionSplitScreenService.isSplitScreenDisabled$.next(false);
          this.activeAgentMessage = undefined;
          this.isAwaitingAgentMessage$.next(false);
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (response: CompanionConversationModel) => {
          const isNewConversation = this.isNewConversation;

          if (!this.conversationId && response?.conversationId) {
            this.conversationId = response.conversationId;
            this.isNewConversation = false;
            this.isInputDisabled$.next(false);

            this.companionSplitScreenService.updateConversationId(this.conversationId);
          }

          if (isNewConversation && !this.splitScreen) {
            this.skipNextLoad = true;
            this.selectConversation(this.conversationId);
          }

          this.replaceMessage(messageTyping, this.buildAgentMessageFromResponse(response));
          this.companionService.notifyConversationUpdated();
        },
        error: (err) => {
          const isNewConversation = this.isNewConversation;
          this.isInputDisabled$.next(false);

          if (isNewConversation) {
            this.toastrService.error(this.translateService.instant(this.companionChatService.getErrorKey(err)));
            this.pushMessages([]);
            return;
          }

          this.replaceMessage(messageTyping, this.buildSystemErrorMessage(err));
        },
      });

    this.activeAgentMessage = { subscription, rollback };
  }

  public onFormResolved(event: ICompanionChatMessageFormResolvedEvent): void {
    const { message, values, comment } = event;
    if (!message.form || !this.conversationId || this.activeAgentMessage) {
      return;
    }

    const messagesBefore = this.messages$.value;
    const sourceForm = message.form;

    const messagesBeforeAdjusted = messagesBefore.map((m) => (m.id === message.id ? { ...m, form: undefined } : m));
    const messageUser = this.companionChatService.createUserMessageByOperation(messagesBefore.length, sourceForm, values);
    const messageAgentTyping = this.companionChatService.createAgentTypingMessage(messagesBefore.length + 1);
    this.pushMessages([...messagesBeforeAdjusted, messageUser, messageAgentTyping]);

    const source = messagesBefore.find((m) => m.id === message.id);
    const responseValues = values ? (message.formResolver?.getFormSubmissionValues(sourceForm, values) ?? values) : values;
    const labels = responseValues ? message.formResolver?.getReferenceLabels(sourceForm, responseValues) : undefined;
    const body = this.companionChatService.buildResponse(
      sourceForm.operation,
      source?.requestId,
      this.route,
      responseValues,
      comment,
      labels,
    );

    const rollback = (): void => {
      const rolledBack = this.messages$.value
        .filter((m) => m.id !== messageAgentTyping.id && m.id !== messageUser.id)
        .map((m) => (m.id === message.id ? { ...m, form: sourceForm } : m));
      this.pushMessages(rolledBack);
    };
    this.companionSplitScreenService.isSplitScreenDisabled$.next(true);
    this.isAwaitingAgentMessage$.next(true);
    const subscription = this.companionService
      .postConversation(body, this.conversationId)
      .pipe(
        takeUntil(this.conversationChange$),
        finalize(() => {
          this.companionSplitScreenService.isSplitScreenDisabled$.next(false);
          this.activeAgentMessage = undefined;
          this.isAwaitingAgentMessage$.next(false);
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (response) => {
          const withoutTyping = this.messages$.value.filter((m) => m.id !== messageAgentTyping.id);
          this.pushMessages([...withoutTyping, { ...this.buildAgentMessageFromResponse(response), dateTime: messageAgentTyping.dateTime }]);
          this.companionService.notifyConversationUpdated();
        },
        error: (err) => {
          const rolledBack = this.messages$.value
            .filter((m) => m.id !== messageAgentTyping.id && m.id !== messageUser.id)
            .map((m) => (m.id === message.id ? { ...m, form: sourceForm } : m));
          this.pushMessages(rolledBack);
          this.toastrService.error(this.translateService.instant(this.companionChatService.getErrorKey(err)));
        },
      });

    this.activeAgentMessage = { subscription, rollback };
  }

  public loadMore(): void {
    if (!this.messagesState.skip || !this.conversationId) {
      return;
    }
    this.loadTrigger$.next({ reset: false });
  }

  public getAuthorInfo(message: IChatMessage): CompanionDocumentSourceModel[] | null {
    return (message as CompanionChatMessage).sources ?? null;
  }

  public closeSplitScreen(): void {
    this.isChatCollapsed = false;
    this.companionSplitScreenService.deactivate();
  }

  public toggleCollapse(): void {
    this.isChatCollapsed = !this.isChatCollapsed;
  }

  public startNewChat(): void {
    this.conversationChange$.next();
    this.cancelActiveAgentMessage();
    this.isInputDisabled$.next(false);
    this.messagesState = new CompanionChatMessagesState();
    this.companionSplitScreenService.activate({ conversationId: null, messages: [] });
  }

  public onCancelAgentMessage(): void {
    this.cancelActiveAgentMessage();
  }

  public toggleSplitScreenMode(): void {
    if (this.splitScreen) {
      this.activateFullScreen();
    } else {
      this.companionSplitScreenService.activateSplitScreen({ conversationId: this.conversationId, messages: this.messages$.value });
    }
  }

  public onFormActionInvoked(action: IChatFormFieldAction): void {
    if (!this.splitScreen) {
      this.companionSplitScreenService.activate({ conversationId: this.conversationId, messages: this.messages$.value });
    }
    this.router.navigate(action.routerLink, { queryParams: action.queryParams });
  }

  private cancelActiveAgentMessage(): void {
    const active = this.activeAgentMessage;
    if (!active) {
      return;
    }
    active.subscription.unsubscribe();
    this.isInputDisabled$.next(false);
    this.companionSplitScreenService.isSplitScreenDisabled$.next(false);
    active.rollback();
  }

  private initialize(): void {
    this.loadTrigger$
      .pipe(
        switchMap(({ reset }) => {
          if (reset) {
            this.messagesState = new CompanionChatMessagesState();
            this.pushMessages([]);
            this.isLoading$.next(true);
          }

          this.companionSplitScreenService.isSplitScreenDisabled$.next(true);
          return this.companionService
            .getConversationMessages(this.conversationId, this.messagesState.pageSize, this.messagesState.skip ?? undefined)
            .pipe(
              tap((response) => {
                this.messagesState.skip = response.continuationToken ?? null;
                const incoming = this.companionChatService.mapResponseToMessages(response.messages, !!this.messagesState.skip);
                if (reset) {
                  this.pushMessages(incoming);
                  this.isLoading$.next(false);
                } else {
                  this.pushMessages([...incoming, ...this.messages$.value]);
                }
              }),
              catchError((err) => {
                if (reset) {
                  const messageNew = this.companionChatService.createSystemErrorMessage(
                    this.newMessageIndex,
                    this.companionChatService.getErrorKey(err),
                  );
                  this.pushMessages([messageNew]);
                  this.isLoading$.next(false);
                }
                return EMPTY;
              }),
              takeUntil(this.conversationChange$),
              finalize(() => {
                this.isLoading$.next(false);
                this.companionSplitScreenService.isSplitScreenDisabled$.next(false);
              }),
            );
        }),
        untilDestroyed(this),
      )
      .subscribe();

    if (this.splitScreen) {
      this.initializeSplitScreen();
    } else {
      this.initializeFullScreen();
    }
  }

  private initializeFullScreen(): void {
    this.companionSplitScreenService.splitScreenRequested$.pipe(untilDestroyed(this)).subscribe(() => this.toggleSplitScreenMode());

    if (this.companionSplitScreenService.isActive) {
      this.companionSplitScreenService.deactivate();
    }

    this.route.paramMap
      .pipe(
        map((params) => params.get('id') ?? null),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((conversationId) => {
        this.conversationChange$.next();
        const isNew = !conversationId || conversationId === NEW_CONVERSATION_ID;

        if (!isNew) {
          if (!this.companionSplitScreenService.isActive) {
            this.companionSplitScreenService.clearState();
          }
          this.conversationId = conversationId;
          this.isNewConversation = false;

          if (this.skipNextLoad) {
            this.skipNextLoad = false;
          } else {
            this.loadTrigger$.next({ reset: true });
          }
        } else {
          this.conversationId = null;
          this.isNewConversation = true;

          const state = this.companionSplitScreenService.state;
          if (state) {
            this.conversationId = state.conversationId;
            this.isNewConversation = !state.conversationId;
            this.pushMessages(state.messages);
            this.companionSplitScreenService.clearState();
          } else {
            const activeConversationId = this.companionSplitScreenService.conversationId;
            if (activeConversationId) {
              this.selectConversation(activeConversationId);
              return;
            }
            this.pushMessages([]);
          }
        }
      });
  }

  private initializeSplitScreen(): void {
    const state = this.companionSplitScreenService.state;
    if (state) {
      this.conversationId = state.conversationId;
      this.isNewConversation = !state.conversationId;
      this.pushMessages(state.messages);
    } else {
      const activeConversationId = this.companionSplitScreenService.conversationId;
      if (activeConversationId) {
        this.conversationId = activeConversationId;
        this.isNewConversation = false;
        this.loadTrigger$.next({ reset: true });
      } else {
        this.conversationId = null;
        this.isNewConversation = true;
        this.pushMessages([]);
      }
    }

    this.companionSplitScreenService.state$.pipe(filter(Boolean), untilDestroyed(this)).subscribe((x) => {
      this.conversationId = x.conversationId;
      this.isNewConversation = !x.conversationId;
      this.pushMessages(x.messages);
    });

    this.companionSplitScreenService.collapseRequested$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.isChatCollapsed) {
        return;
      }
      this.isChatCollapsed = true;
      this.changeDetectorRef.markForCheck();
    });

    this.companionSplitScreenService.expandRequested$.pipe(untilDestroyed(this)).subscribe(() => this.activateFullScreen());
  }

  private activateFullScreen(): void {
    this.companionSplitScreenService.startExpand({ conversationId: this.conversationId, messages: this.messages$.value });
    const id = this.conversationId || NEW_CONVERSATION_ID;
    this.router.navigate([`/${AppRoutesShared.CompanionChat}`, id]);
  }

  private buildAgentMessageFromResponse(response: CompanionConversationModel): CompanionChatMessage {
    if (!response?.message) {
      return this.buildSystemErrorMessage(null);
    }
    const msg = response.message;
    this.companionChatService.notifyEntitiesChanged(response);
    return this.companionChatService.buildAgentMessage(
      this.newMessageIndex,
      msg.content,
      msg.toolCalls,
      msg.messageType,
      response.requestId,
      msg.form,
      msg.error,
    );
  }

  private buildSystemErrorMessage(err: unknown): CompanionChatMessage {
    return this.companionChatService.createSystemErrorMessage(this.newMessageIndex, this.companionChatService.getErrorKey(err));
  }

  private replaceMessage(messageFrom: IChatMessage, messageTo: CompanionChatMessage): void {
    const messages = this.messages$.value.filter((x) => x.id !== messageFrom.id);
    this.pushMessages([...messages, { ...messageTo, dateTime: messageFrom.dateTime }]);
  }

  private selectConversation(conversationId: string): void {
    this.router.navigate([conversationId], { relativeTo: this.route.parent });
  }

  private pushMessages(messages: CompanionChatMessage[]): void {
    const messagesSupported = messages.map((message) =>
      message.form && message.form.operation !== ChatFormOperation.Confirm && !this.formResolver.hasHandler(message.form.entity)
        ? { ...message, form: undefined, formResolver: undefined }
        : message,
    );
    const built = this.companionChatService.buildChatMessages(messagesSupported);
    for (const message of built) {
      if (message.form && !message.formResolver) {
        message.form = this.configureTenantFields(message.form);
        message.formResolver = new ChatMessageFormResolverProxy(this.formResolver);
      }
    }
    this.messages$.next(built);
  }

  private configureTenantFields(form: IChatForm): IChatForm {
    if (!form.fields.some((field) => field.referenceType === CompanionReferenceType.Tenant)) {
      return form;
    }

    const resolution = this.companionChatResolver.resolveTenant();
    return { ...form, fields: form.fields.map((field) => this.configureTenantField(field, resolution)) };
  }

  private configureTenantField(field: IChatFormField, resolution: ReturnType<CompanionChatResolver['resolveTenant']>): IChatFormField {
    if (field.referenceType !== CompanionReferenceType.Tenant) {
      return field;
    }
    if (resolution.mode === ChatFormTenantMode.Fixed) {
      return { ...field, type: ChatFormFieldType.Hidden, valueDefault: resolution.tenantId, controlComponent: undefined };
    }
    return { ...field, type: ChatFormFieldType.Control, controlComponent: resolution.component };
  }
}
